import { useMemo, useCallback } from 'react';
import { RxCollection } from 'rxdb';
import { useRxData } from 'rxdb-hooks';

import { escapeRegExp } from '@helper/utils';
import { useDebounceSearch } from '@hooks/useDebounceSearch';
import { useRxCountQuery } from '@hooks/useCount';
import { useFilterStore } from '@store/filter';

import { MdpMeetingsFilter } from '..';
import { MDPMeetingObject } from '../mdp-meeting-card';
import { IMeetingReturn, MdpMeetingFilterObject } from '.';
import { useUserRoleType } from '@hooks/useUserRoleType';

const MONTHS = [
	'January',
	'February',
	'March',
	'April',
	'May',
	'June',
	'July',
	'August',
	'September',
	'October',
	'November',
	'December',
];

const currentMonthIndex = new Date().getMonth();

const CURRENT_MONTH_ORDER = MONTHS.reduce<Record<string, number>>((acc, month, index) => {
	const order = (index - currentMonthIndex + 12) % 12;
	acc[month] = order;
	return acc;
}, {});

export const MdpMeetings = (filter: MdpMeetingFilterObject): IMeetingReturn => {
	const userRole = useUserRoleType();
	const { search, sortValue } = useFilterStore();

	const debouncedSearch = useDebounceSearch(search);

	const collectionName = 'mdp_meeting_list';

	const queryConstructor = useCallback(
		(collection: RxCollection<MDPMeetingObject>) => {
			const selector: MdpMeetingsFilter = {};

			if (debouncedSearch.trim()) {
				selector.name = {
					$regex: `.*${escapeRegExp(debouncedSearch)}.*`,
					$options: 'i',
				};
			}

			if (filter.mdicode) {
				selector.mdicode_c = { $eq: filter.mdicode };
			}

			if (filter.meetingType) {
				selector.meeting_type_c = { $eq: filter.meetingType };
			}

			if (filter.month?.trim()) {
				selector.monthname_c = { $eq: filter.month };
			}
			const clubData = filter.club?.map((item) => item.value) ?? [];

			if (clubData.length) {
				selector.cmditownlist_c = {
					$regex: `.*${escapeRegExp(clubData.join(','))}.*`,
					$options: 'i',
				};
			}

			if (filter.meetingStatus && filter.meetingStatus == 'Assigned') {
				selector.number_of_planned_meetings_c = 1;
				selector.actual_number_of_planned_meetings_c = 0;
			}

			if (filter.meetingStatus && filter.meetingStatus == 'Planned') {
				selector.number_of_planned_meetings_c = 1;
				selector.actual_number_of_planned_meetings_c = 1;
			}

			const defaultSelector = Object.keys(selector).length > 0 ? selector : {};

			let query = collection.find({
				selector: defaultSelector,
			});

			if (sortValue && sortValue !== 'monthname_c') {
				const isSpecialSort = ['mdicode_c', 'meeting_type_c', 'createddate'].includes(sortValue);

				query = query.sort({
					[sortValue]: isSpecialSort || sortValue === 'createddate' ? 'asc' : 'desc',
				});
			} else {
				query = query.sort({
					createddate: 'desc',
				});
			}

			return query;
		},
		[filter, debouncedSearch, sortValue]
	);

	const totalCount = useRxCountQuery<MDPMeetingObject>(collectionName, queryConstructor);

	const { result: allMeetings } = useRxData(collectionName, queryConstructor, {
		pageSize: 0,
		pagination: 'Infinite',
	});

	const meetingTotals = useMemo(() => {
		const plannedMeeting = allMeetings.reduce((sum, meeting) => sum + (meeting.number_of_planned_meetings_c || 0), 0);

		const actualMeeting = allMeetings.reduce(
			(sum, meeting) => sum + (meeting.actual_number_of_planned_meetings_c || 0),
			0
		);

		return {
			plannedMeeting,
			actualMeeting,
		};
	}, [allMeetings]);

	const { result, isFetching, fetchMore } = useRxData(collectionName, queryConstructor, {
		pageSize: 20,
		pagination: 'Infinite',
	});

	const sortedResult = useMemo(() => {
		if (!result) return [];

		const data = [...result];

		// Non-BDE users -> keep RxDB order (createddate desc by default)
		if (userRole !== 'BDE') {
			return data;
		}

		// BDE custom sorts -> keep RxDB order
		if (sortValue === 'createddate' || sortValue === 'mdicode_c' || sortValue === 'meeting_type_c') {
			return data;
		}

		// BDE -> month-wise chronological sorting
		return data.sort((a, b) => {
			const monthDiff = (CURRENT_MONTH_ORDER[a.monthname_c] ?? 99) - (CURRENT_MONTH_ORDER[b.monthname_c] ?? 99);

			if (monthDiff !== 0) {
				return monthDiff;
			}

			return new Date(b.createddate).getTime() - new Date(a.createddate).getTime();
		});
	}, [result, sortValue, userRole]);

	return {
		result: sortedResult,
		isFetching,
		fetchMore,
		totalCount,
		plannedMeeting: meetingTotals.plannedMeeting,
		actualMeeting: meetingTotals.actualMeeting,
	};
};
